import { DecisionGroupCircles, UserAvatar } from "@/components/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Role, SortOrder, TenderSortField, Tender, GetPaginatedTendersQuery, UserOnEntity, TenderState, GetPaginatedTendersDocument, GetPaginatedTendersQueryVariables } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { formatNumberLocale, getUserFriendlyTenderState, handleTenderStateColour } from "@/utils/helpers";
import { InformationCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { addDays, differenceInDays } from "date-fns";
import { JSX, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useQuery } from "@apollo/client/react";

interface DaysUntilDueProps {
  deadline: Date;
}

/**
 * DaysUntilDue component renders the number of days remaining until the submission deadline.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Date} props.deadline - The submission deadline date.
 * @returns {JSX.Element} A div containing the due label and days remaining.
 */
const DaysUntilDue: React.FC<DaysUntilDueProps> = ({ deadline }: DaysUntilDueProps): JSX.Element => {

  const { t } = useTranslation(["components", "common", "tables"]);
  const daysRemaining = differenceInDays(new Date(deadline), new Date());
  const colourClass = daysRemaining > 10 ? "text-yellow-500" : daysRemaining > 5 ? "text-orange-500" : "text-red-500";

  return (
    <div className="flex gap-1 ml-[2px]">
      <h1 className="font-light text-sm">
        {t("upcomingtendersubmissions.labels.due")}
      </h1>
      <p className={colourClass}>
        {daysRemaining} {t("common:days")}
      </p>
    </div>
  );
};

/**
 * UpcomingTenderSubmissions component displays a list of upcoming tender submissions.
 * It fetches the data using the `useGetUrgentPaginatedTendersQuery` hook and displays a loading
 * state, a no data message, or the list of tenders based on the query result.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <UpcomingTenderSubmissions />
 *
 * @remarks
 * This component uses the following hooks:
 * - `useIntl` for internationalization.
 * - `useNavigate` for navigation.
 * - `useTranslation` for translations.
 * - `useGetUrgentPaginatedTendersQuery` for fetching urgent paginated tenders.
 *
 */
const UpcomingTenderSubmissions: React.FC = (): JSX.Element => {

  // Hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { t } = useTranslation(["components", "common", "tables"]);
  const { userId } = useUserStore();
  const userRole = useUserStore((state) => state.userRole);

  // State
  const [showAll, setShowAll] = useState<boolean>(false);

  // Helper function to check if user is manager
  const isUserManager = userRole === Role.Admin || userRole === Role.Manager;

  // Memoize query variables to prevent infinite loops caused by new Date() objects
  const queryVariables = useMemo(() => {
    const today = new Date().toISOString();
    const twoWeeksFromNow = addDays(today, 14).toISOString();

    return {
      first: 10,
      after: null,
      filters: {
        deadlineDate: {
          lte: twoWeeksFromNow,
          gte: today,
        },
        state: [
          TenderState.Editing, TenderState.PendingBidDecision, TenderState.PendingMarginDecision
        ],
        // Only filter by userId if it's valid and not a manager showing all
        chanceAdmins: isUserManager || showAll || userId == null ? undefined : [userId]
      },
      sort: {
        field: TenderSortField.DeadlineSubmission,
        order: SortOrder.Asc
      },
    };
  }, [showAll, userId, isUserManager]); // Add isUserManager to dependency array

  // Query hooks
  const { data, loading } = useQuery<GetPaginatedTendersQuery, GetPaginatedTendersQueryVariables>(GetPaginatedTendersDocument, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  /**
   * Handles the click event for a tender item by navigating
   * to the executive summary page for the selected tender.
   *
   * @param tender - The tender object whose executive summary should be displayed.
   */
  const handleViewClick = (tender: Tender): void => {
    navigate(`/tender/${tender.id}`);
  };

  return (
    <Card className="flex flex-col col-span-1 space-y-2 h-full">
      <CardHeader className="pb-2">
        <CardTitle className="pl-2">
          <div className="text-base flex justify-between items-center">
            {t("upcomingtendersubmissions.title")}
            <div className="flex items-center gap-2">
              {!isUserManager && userId != null && (
                <div className="flex items-center gap-2 mt-">
                  <Switch
                    checked={showAll}
                    onCheckedChange={setShowAll}
                  />
                  <Label>{t("upcomingtendersubmissions.labels.showall")}</Label>
                </div>
              )}
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 p-0 hover:cursor-help"
                  >
                    <InformationCircleIcon className="h-5 w-5" />
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-80 text-sm font-normal">
                  {t("upcomingtendersubmissions.tooltip")}
                </HoverCardContent>
              </HoverCard>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="h-full pr-3">
        <div className="flex flex-col gap-2 h-full">
          {
            loading ? (
              Array.from({length: 5}).map((_, index) => (
                <div className="w-full pr-2" key={`tender-submission-loading-${index}`}>
                  <div className="flex items-center justify-between">
                    <Skeleton className="w-48 h-4" />
                    <Skeleton className="w-16 h-6" />
                  </div>
                  <Skeleton className="w-40 h-4" />
                  <div className="flex items-center justify-between py-2">
                    <Skeleton className="w-24 h-4" />
                    <Skeleton className="w-24 h-4" />
                  </div>
                </div>
              ))
              // No data message
            ) : (data?.paginatedTenders.edges.length === 0 || !data) ? (
              <div className="text-sm text-gray-500 text-center">
                {t("common:nodataavailable")}
              </div>
            ) : (
            // Scroll container
              <ScrollArea className="flex flex-col gap-2 max-h-[300px] lg:max-h-none lg:h-[calc(100vh-11.75rem)] pr-3">
                {data?.paginatedTenders?.edges.map((edge) => edge.node).map((tender) => (
                  <div
                    className="pt-1 pb-2 text-sm border-b cursor-pointer hover:bg-gray-50 rounded px-2"
                    key={`urgent-tender-${tender.id}`}
                    onClick={() => handleViewClick(tender as Tender)}
                  >
                    <div className="mt-2">
                      <div className="flex items-center justify-between gap-4 mb-1">
                        <div className="flex items-center gap-1">
                          <div className="group relative">
                            <UserAvatar user={tender.description.owner as UserOnEntity} />
                            <Card className="hidden absolute -bottom-8 left-0 w-fit p-1 text-sm group-hover:block z-10">
                              <p className="text-center text-slate-800 py-1 whitespace-nowrap">
                                {tender.description.owner?.firstname}{" "}{tender.description.owner?.lastname}
                              </p>
                            </Card>
                          </div>
                          <p className="font-base wrap-break-words overflow-hidden text-ellipsis line-clamp-2 max-w-[200px]">
                            {tender.description.name}
                          </p>
                        </div>
                        {/* Operational Organizations */}
                        <DecisionGroupCircles groups={tender.bidDecisionGroups} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      {/* Days until due */}
                      <DaysUntilDue deadline={new Date(tender.deadline.submission)} />
                      {/* Revenue */}
                      <div className="flex flex-col">
                        <p className="font-base">{formatNumberLocale(intl, tender.revenue, "currency", false, false)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {
                        tender.hasRecentMessages && (
                          <div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <ExclamationCircleIcon className="size-5 text-red-500"/>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="w-auto">
                                  {t("upcomingtendersubmissions.recentmessages")}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )
                      }
                      <Badge className={cn(handleTenderStateColour(tender.state, true, false, false), "state-badge text-[10px]")}>
                        {getUserFriendlyTenderState(t, tender.state)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            )
          }
        </div>
      </CardContent>
    </Card>
  );
};

export default UpcomingTenderSubmissions;
